import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import CircularProgress from '@material-ui/core/CircularProgress';

// CONSTANTS
import { contentStatuses } from '@/constants/watchList';

// HOC COMPONENTS
import CheckAuth from '@/hoc/CheckAuth';

// STYLES
import { ButtonStyled } from './styles';

const AddRemoveButton = props => {
  const {
    added,
    loading,
    handleDelete,
    minWidth,
    checkAuth,
    checkAuthProps,
    removeText,
    addedColor,
    hideAddedIcon,
    status,
    ...rest
  } = props;
  const [t] = useTranslation();
  const [active, setActive] = useState(false);
  const buttonProps = {
    fullWidth: true,
    ...rest,
  };
  let statusIcon = null;

  if (added) {
    buttonProps.onMouseEnter = () => setActive(true);
    buttonProps.onMouseLeave = () => setActive(false);
    buttonProps.startIcon =
      !hideAddedIcon && !status ? <i className={active ? 'icon-delete' : 'icon-check'} /> : null;
    buttonProps.color = addedColor || 'primary';
    buttonProps.variant = 'contained';
    buttonProps.onClick = e => {
      setActive(false);
      handleDelete(e);
    };
  }

  if (active) {
    buttonProps.children = removeText || t('buttons.remove');
  }

  if (loading) {
    buttonProps.startIcon = <CircularProgress color="inherit" size={16} />;
  }

  if (buttonProps.disabled) {
    buttonProps.color = 'default';
    buttonProps.variant = 'contained';
  }

  if (status) {
    switch (status) {
      case contentStatuses.watched:
        statusIcon = <i className={added ? 'icon-eye' : 'icon-eye-close'} />;
        break;
      case contentStatuses.currentlyWatching:
        statusIcon = <i className={added ? 'icon-movie-filled' : 'icon-movie'} />;
        break;
      case contentStatuses.notInterested:
        statusIcon = <i className={added ? 'icon-face-sad' : 'icon-not-intrested'} />;
        break;
      default:
        break;
    }
  }

  buttonProps.className = clsx('add-remove-btn', buttonProps.className, {
    'add-remove-btn--active': added && active,
    'add-remove-btn--with-status': !!status,
    loading,
  });

  const button = (
    <ButtonStyled {...buttonProps} style={{ minWidth: `${minWidth}px` }}>
      {!!statusIcon && <span className="add-remove-btn__icon">{statusIcon}</span>}
      {buttonProps.children}
    </ButtonStyled>
  );

  return checkAuth ? <CheckAuth {...checkAuthProps}>{button}</CheckAuth> : <>{button}</>;
};

AddRemoveButton.defaultProps = {
  added: false,
  loading: false,
  handleDelete: () => {},
  minWidth: 200,
  checkAuth: false,
  checkAuthProps: {},
  removeText: '',
  addedColor: '',
  hideAddedIcon: false,
  status: null,
};

AddRemoveButton.propTypes = {
  added: PropTypes.bool,
  loading: PropTypes.bool,
  handleDelete: PropTypes.func,
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  checkAuth: PropTypes.bool,
  checkAuthProps: PropTypes.object,
  removeText: PropTypes.string,
  addedColor: PropTypes.string,
  hideAddedIcon: PropTypes.bool,
  status: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
};

export default React.memo(AddRemoveButton);
